import { useState, useEffect } from 'react'
import * as React from 'react'
import { Head, router } from '@inertiajs/react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { TagLabel } from '@/components/ui/tag-label'
import { Switch } from '@/components/ui/switch'
import WorkflowModal from '@/components/WorkflowModal'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ArrowLeft, Edit, Trash2, Mail, Plus, ArrowDown, MoveDown, Pencil, MoreVertical, Settings } from 'lucide-react'
import { Filter } from '@/types/filter'
import { FilterCard } from '@/components/FilterCard'
import { FilterEditor } from '@/components/FilterEditor'
import { Action } from '@/types/action'
import { ActionCard } from '@/components/ActionCard'
import { ActionEditor } from '@/components/ActionEditor'
import WorkflowStats from '@/components/WorkflowStats'

interface Workflow {
  id: number
  name: string
  description: string | null
  order: number
  isActive: boolean
  stopProcessingOnMatch: boolean
  stopProcessingOnFail: boolean
  requireOneOptionalFilterToPass: boolean
  stats: {
    emailsProcessed: number
    emailsMatched: number
    emailsFailed: number
    matchRate: number
    failRate: number
    lastProcessedAt: string | null
  }
  filters: Filter[]
  actions: Action[]
}

interface ShowProps {
  account: {
    id: number
    name: string
  }
  workflow: Workflow
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
}

// Sortable Filter Card wrapper
function SortableFilterCard({
  filter,
  onEdit,
  onDelete,
}: {
  filter: Filter
  onEdit: () => void
  onDelete: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `filter-${filter.id}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <FilterCard
        filter={filter}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

// Sortable Action Card wrapper
function SortableActionCard({
  action,
  onEdit,
  onDelete,
}: {
  action: Action
  onEdit: () => void
  onDelete: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `action-${action.id}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ActionCard
        action={action}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

export default function Show({
  account,
  workflow,
  currentUser,
  gmailConnection,
}: ShowProps) {
  const [isToggling, setIsToggling] = useState(false)
  const [localIsActive, setLocalIsActive] = useState(workflow.isActive)
  const [localStopProcessingOnMatch, setLocalStopProcessingOnMatch] = useState(workflow.stopProcessingOnMatch)
  const [localStopProcessingOnFail, setLocalStopProcessingOnFail] = useState(workflow.stopProcessingOnFail)
  const [localRequireOneOptionalFilterToPass, setLocalRequireOneOptionalFilterToPass] = useState(workflow.requireOneOptionalFilterToPass)
  const [isHoveringTitle, setIsHoveringTitle] = useState(false)
  const [isHoveringDescription, setIsHoveringDescription] = useState(false)

  // Sync local state with prop when workflow changes
  useEffect(() => {
    setLocalIsActive(workflow.isActive)
    setLocalStopProcessingOnMatch(workflow.stopProcessingOnMatch)
    setLocalStopProcessingOnFail(workflow.stopProcessingOnFail)
    setLocalRequireOneOptionalFilterToPass(workflow.requireOneOptionalFilterToPass)
  }, [workflow.isActive, workflow.stopProcessingOnMatch, workflow.stopProcessingOnFail, workflow.requireOneOptionalFilterToPass])

  const handleToggleActive = (checked: boolean) => {
    // Optimistically update local state immediately
    setLocalIsActive(checked)
    setIsToggling(true)

    router.patch(
      `/accounts/${account.id}/workflows/${workflow.id}`,
      {
        workflow: {
          is_active: checked,
        },
      },
      {
        preserveScroll: true,
        only: ['workflow'],
        onFinish: () => {
          setIsToggling(false)
          // Reset to prop value if update failed
          setLocalIsActive(workflow.isActive)
        },
        onError: () => {
          // Revert on error
          setLocalIsActive(workflow.isActive)
          setIsToggling(false)
        },
      }
    )
  }
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [filtersList, setFiltersList] = useState<Filter[]>(workflow.filters || [])
  const [editingFilterId, setEditingFilterId] = useState<number | null>(null)
  const [creatingFilter, setCreatingFilter] = useState(false)
  const [savingFilter, setSavingFilter] = useState(false)
  const [deleteFilterId, setDeleteFilterId] = useState<number | null>(null)
  const [actionsList, setActionsList] = useState<Action[]>(workflow.actions || [])
  const [editingActionId, setEditingActionId] = useState<number | null>(null)
  const [creatingAction, setCreatingAction] = useState(false)
  const [savingAction, setSavingAction] = useState(false)
  const [deleteActionId, setDeleteActionId] = useState<number | null>(null)

  // Sync filters and actions when workflow updates
  useEffect(() => {
    setFiltersList(workflow.filters || [])
    setActionsList(workflow.actions || [])
  }, [workflow.filters, workflow.actions])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDelete = () => {
    router.delete(`/accounts/${account.id}/workflows/${workflow.id}`, {
      onSuccess: () => {
        // Redirect to workflows index after successful deletion
        router.visit(`/accounts/${account.id}/workflows`)
      },
      onError: () => {
        console.error('Failed to delete workflow')
      }
    })
  }

  const handleCloseModal = () => {
    setShowEditModal(false)
    router.reload({ only: ['workflow'] })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const activeId = active.id.toString()
    const overId = over.id.toString()

    // Handle filter reordering
    if (activeId.startsWith('filter-') && overId.startsWith('filter-')) {
      const activeFilterId = parseInt(activeId.replace('filter-', ''))
      const overFilterId = parseInt(overId.replace('filter-', ''))

      setFiltersList((items) => {
        const oldIndex = items.findIndex((item) => item.id === activeFilterId)
        const newIndex = items.findIndex((item) => item.id === overFilterId)
        const newItems = arrayMove(items, oldIndex, newIndex)

        // Update order values
        const reorderedFilters = newItems.map((item, index) => ({
          ...item,
          order: index + 1,
        }))

        // Send reorder request to server
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

        fetch(`/accounts/${account.id}/workflows/${workflow.id}/filters/reorder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
          body: JSON.stringify({
            filters: reorderedFilters.map((f) => ({
              id: f.id,
              order: f.order,
            })),
          }),
        }).catch((error) => {
          console.error('Failed to reorder filters:', error)
          router.reload({ only: ['workflow'] })
        })

        return reorderedFilters
      })
    }

    // Handle action reordering
    if (activeId.startsWith('action-') && overId.startsWith('action-')) {
      const activeActionId = parseInt(activeId.replace('action-', ''))
      const overActionId = parseInt(overId.replace('action-', ''))

      setActionsList((items) => {
        const oldIndex = items.findIndex((item) => item.id === activeActionId)
        const newIndex = items.findIndex((item) => item.id === overActionId)
        const newItems = arrayMove(items, oldIndex, newIndex)

        // Update order values
        const reorderedActions = newItems.map((item, index) => ({
          ...item,
          order: index + 1,
        }))

        // Send reorder request to server
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

        fetch(`/accounts/${account.id}/workflows/${workflow.id}/actions/reorder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
          body: JSON.stringify({
            actions: reorderedActions.map((a) => ({
              id: a.id,
              order: a.order,
            })),
          }),
        }).catch((error) => {
          console.error('Failed to reorder actions:', error)
          router.reload({ only: ['workflow'] })
        })

        return reorderedActions
      })
    }
  }

  const handleCreateFilter = () => {
    setCreatingFilter(true)
    setEditingFilterId(null)
  }

  const handleEditFilter = (filterId: number) => {
    setEditingFilterId(filterId)
    setCreatingFilter(false)
  }

  const handleSaveFilter = async (filterData: Partial<Filter>) => {
    setSavingFilter(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    try {
      if (editingFilterId) {
        // Update existing filter
        const response = await fetch(
          `/accounts/${account.id}/workflows/${workflow.id}/filters/${editingFilterId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken || '',
            },
            body: JSON.stringify({ filter: filterData }),
          }
        )
        const updatedFilter = await response.json()
        setFiltersList((filters) =>
          filters.map((f) => (f.id === editingFilterId ? updatedFilter : f))
        )
        setEditingFilterId(null)
      } else {
        // Create new filter
        const response = await fetch(
          `/accounts/${account.id}/workflows/${workflow.id}/filters`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken || '',
            },
            body: JSON.stringify({ filter: filterData }),
          }
        )
        const newFilter = await response.json()
        setFiltersList((filters) => [...filters, newFilter])
        setCreatingFilter(false)
      }
    } catch (error) {
      console.error('Failed to save filter:', error)
    } finally {
      setSavingFilter(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingFilterId(null)
    setCreatingFilter(false)
  }

  const handleDeleteFilter = async (filterId: number) => {
    setDeleteFilterId(filterId)
  }

  const confirmDeleteFilter = async () => {
    if (!deleteFilterId) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    try {
      await fetch(
        `/accounts/${account.id}/workflows/${workflow.id}/filters/${deleteFilterId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
        }
      )
      setFiltersList((filters) =>
        filters
          .filter((f) => f.id !== deleteFilterId)
          .map((f, index) => ({ ...f, order: index + 1 }))
      )
    } catch (error) {
      console.error('Failed to delete filter:', error)
    } finally {
      setDeleteFilterId(null)
    }
  }

  const handleCreateAction = () => {
    setCreatingAction(true)
    setEditingActionId(null)
  }

  const handleEditAction = (actionId: number) => {
    setEditingActionId(actionId)
    setCreatingAction(false)
  }

  const handleSaveAction = async (actionData: Partial<Action>) => {
    setSavingAction(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    try {
      if (editingActionId) {
        // Update existing action
        const response = await fetch(
          `/accounts/${account.id}/workflows/${workflow.id}/actions/${editingActionId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken || '',
            },
            body: JSON.stringify({ action_data: actionData }),
          }
        )
        const updatedAction = await response.json()
        setActionsList((actions) =>
          actions.map((a) => (a.id === editingActionId ? updatedAction : a))
        )
        setEditingActionId(null)
      } else {
        // Create new action
        const response = await fetch(
          `/accounts/${account.id}/workflows/${workflow.id}/actions`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken || '',
            },
            body: JSON.stringify({ action_data: actionData }),
          }
        )
        const newAction = await response.json()
        setActionsList((actions) => [...actions, newAction])
        setCreatingAction(false)
      }
    } catch (error) {
      console.error('Failed to save action:', error)
    } finally {
      setSavingAction(false)
    }
  }

  const handleCancelEditAction = () => {
    setEditingActionId(null)
    setCreatingAction(false)
  }

  const handleDeleteAction = async (actionId: number) => {
    setDeleteActionId(actionId)
  }

  const confirmDeleteAction = async () => {
    if (!deleteActionId) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    try {
      await fetch(
        `/accounts/${account.id}/workflows/${workflow.id}/actions/${deleteActionId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
        }
      )
      setActionsList((actions) =>
        actions
          .filter((a) => a.id !== deleteActionId)
          .map((a, index) => ({ ...a, order: index + 1 }))
      )
    } catch (error) {
      console.error('Failed to delete action:', error)
    } finally {
      setDeleteActionId(null)
    }
  }

  const handleToggleStopProcessingOnMatch = (checked: boolean) => {
    setLocalStopProcessingOnMatch(checked)
    setIsToggling(true)

    router.patch(
      `/accounts/${account.id}/workflows/${workflow.id}`,
      {
        workflow: {
          stop_processing_on_match: checked,
        },
      },
      {
        preserveScroll: true,
        only: ['workflow'],
        onFinish: () => {
          setIsToggling(false)
          setLocalStopProcessingOnMatch(workflow.stopProcessingOnMatch)
        },
        onError: () => {
          setLocalStopProcessingOnMatch(workflow.stopProcessingOnMatch)
          setIsToggling(false)
        },
      }
    )
  }

  const handleToggleStopProcessingOnFail = (checked: boolean) => {
    setLocalStopProcessingOnFail(checked)
    setIsToggling(true)

    router.patch(
      `/accounts/${account.id}/workflows/${workflow.id}`,
      {
        workflow: {
          stop_processing_on_fail: checked,
        },
      },
      {
        preserveScroll: true,
        only: ['workflow'],
        onFinish: () => {
          setIsToggling(false)
          setLocalStopProcessingOnFail(workflow.stopProcessingOnFail)
        },
        onError: () => {
          setLocalStopProcessingOnFail(workflow.stopProcessingOnFail)
          setIsToggling(false)
        },
      }
    )
  }

  const handleToggleRequireOneOptionalFilterToPass = (checked: boolean) => {
    setLocalRequireOneOptionalFilterToPass(checked)
    setIsToggling(true)

    router.patch(
      `/accounts/${account.id}/workflows/${workflow.id}`,
      {
        workflow: {
          require_one_optional_filter_to_pass: checked,
        },
      },
      {
        preserveScroll: true,
        only: ['workflow'],
        onFinish: () => {
          setIsToggling(false)
          setLocalRequireOneOptionalFilterToPass(workflow.requireOneOptionalFilterToPass)
        },
        onError: () => {
          setLocalRequireOneOptionalFilterToPass(workflow.requireOneOptionalFilterToPass)
          setIsToggling(false)
        },
      }
    )
  }

  // Check if there are any optional filters
  const hasOptionalFilters = filtersList.some(filter => filter.optional)

  return (
    <>
      <Head title={workflow.name} />
      <DashboardLayout
        currentUser={currentUser}
        gmailConnection={gmailConnection}
        account={account}
        activeTab="workflows"
      >
      <div className="space-y-6">
        {/* Back Link */}
        <Button
          variant="ghost"
          onClick={() => router.get(`/accounts/${account.id}/workflows`)}
          className="p-0 h-auto font-normal text-gray-600 dark:text-gray-400 hover:text-blue-950 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          All workflows
        </Button>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 lg:gap-4">
          <div className="flex-1 min-w-0">
            <div
              className="group relative block"
              onMouseEnter={() => setIsHoveringTitle(true)}
              onMouseLeave={() => setIsHoveringTitle(false)}
              onClick={() => setShowEditModal(true)}
            >
              <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-1 cursor-pointer flex items-center gap-2">
                {workflow.name}
                {isHoveringTitle && (
                  <Pencil className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                )}
              </h1>
            </div>
            {workflow.description && (
              <div
                className="group relative block"
                onMouseEnter={() => setIsHoveringDescription(true)}
                onMouseLeave={() => setIsHoveringDescription(false)}
                onClick={() => setShowEditModal(true)}
              >
                <p className="text-gray-600 dark:text-gray-400 cursor-pointer flex items-center gap-2">
                  {workflow.description}
                  {isHoveringDescription && (
                    <Pencil className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                  )}
                </p>
              </div>
            )}
            {/* Stats */}
            <div className="mt-4">
              <WorkflowStats
                emailsProcessed={workflow.stats.emailsProcessed}
                emailsMatched={workflow.stats.emailsMatched}
                emailsFailed={workflow.stats.emailsFailed}
                matchRate={workflow.stats.matchRate}
                failRate={workflow.stats.failRate}
                filtersCount={filtersList.length}
                actionsCount={actionsList.length}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:gap-8 mt-4 lg:mt-0">
            {/* Toggle Switches */}
            {/* Active Toggle - First */}
            <div className="flex items-center gap-2">
              <Switch
                id="active-toggle"
                checked={localIsActive}
                onCheckedChange={handleToggleActive}
                disabled={isToggling}
              />
              <Label htmlFor="active-toggle" className="text-sm font-medium cursor-pointer">
                Active
              </Label>
            </div>
            {/* Stops on Match Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                id="stop-on-match-toggle"
                checked={localStopProcessingOnMatch}
                onCheckedChange={handleToggleStopProcessingOnMatch}
                disabled={isToggling}
              />
              <Label htmlFor="stop-on-match-toggle" className="text-sm font-medium cursor-pointer">
                Stops on match
              </Label>
            </div>
            {/* Stops on Fail Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                id="stop-on-fail-toggle"
                checked={localStopProcessingOnFail}
                onCheckedChange={handleToggleStopProcessingOnFail}
                disabled={isToggling}
              />
              <Label htmlFor="stop-on-fail-toggle" className="text-sm font-medium cursor-pointer">
                Stops on fail
              </Label>
            </div>
            {/* Require One Optional Filter To Pass Toggle - Only show when there are optional filters */}
            {hasOptionalFilters && (
              <div className="flex items-center gap-2">
                <Switch
                  id="require-optional-toggle"
                  checked={localRequireOneOptionalFilterToPass}
                  onCheckedChange={handleToggleRequireOneOptionalFilterToPass}
                  disabled={isToggling}
                />
                <Label htmlFor="require-optional-toggle" className="text-sm font-medium cursor-pointer">
                  Require 1 optional filter
                </Label>
              </div>
            )}

            {/* Settings Dropdown */}
            <div className="flex items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowEditModal(true)}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-red-600 dark:text-red-400"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Workflow Builder */}
        <Card>
          <CardContent className="pt-6">
            {/* Visual Flow */}
            <div className="space-y-4">
              {/* Entry Point */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border-2 border-blue-600 dark:border-blue-600 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-blue-900 dark:text-blue-100">Email Enters Workflow</span>
                </div>
                <div className="flex justify-center my-4">
                  <MoveDown className="h-12 w-12 text-blue-600" strokeWidth={1.5} />
                </div>
              </div>

              {/* Filter Steps Section */}
              <div className="space-y-3">
                {filtersList.length > 0 ? (
                  <>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={filtersList.map((f) => `filter-${f.id}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-3">
                          {filtersList.map((filter) => {
                            const isEditing = editingFilterId === filter.id

                            if (isEditing) {
                              return (
                                <FilterEditor
                                  key={filter.id}
                                  filter={filter}
                                  onSave={handleSaveFilter}
                                  onCancel={handleCancelEdit}
                                  onDelete={() => handleDeleteFilter(filter.id)}
                                  isSaving={savingFilter}
                                />
                              )
                            }

                            return (
                              <SortableFilterCard
                                key={filter.id}
                                filter={filter}
                                onEdit={() => handleEditFilter(filter.id)}
                                onDelete={() => handleDeleteFilter(filter.id)}
                              />
                            )
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                    {/* Add Filter Button - shown below filters when filters exist */}
                    {!creatingFilter && editingFilterId === null && (
                      <div className="flex justify-center">
                        <Button
                          onClick={handleCreateFilter}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Filter Step
                        </Button>
                      </div>
                    )}
                  </>
                ) : filtersList.length === 0 && !creatingFilter && editingFilterId === null ? (
                  <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-slate-50 dark:bg-gray-800 space-y-4">
                    <p className="text-sm text-slate-500 dark:text-gray-400">
                      No filter steps yet. Add filters to define which emails match this workflow.
                    </p>
                    {/* Add Filter Button - shown inside dashed box when no filters */}
                    <Button
                      onClick={handleCreateFilter}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Filter Step
                    </Button>
                  </div>
                ) : null}

                {/* Create New Filter Form */}
                {creatingFilter && (
                  <FilterEditor
                    filter={{}}
                    onSave={handleSaveFilter}
                    onCancel={handleCancelEdit}
                    isSaving={savingFilter}
                  />
                )}
              </div>

              {/* Connecting Line */}
              <div className="flex justify-center my-4">
                <MoveDown className="h-12 w-12 text-blue-600" strokeWidth={1.5} />
              </div>

              {/* Action Steps Section */}
              <div className="space-y-3">
                {actionsList.length > 0 ? (
                  <>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={actionsList.map((a) => `action-${a.id}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-3">
                          {actionsList.map((action) => {
                            const isEditing = editingActionId === action.id

                            if (isEditing) {
                              return (
                                <ActionEditor
                                  key={action.id}
                                  action={action}
                                  onSave={handleSaveAction}
                                  onCancel={handleCancelEditAction}
                                  onDelete={() => handleDeleteAction(action.id)}
                                  isSaving={savingAction}
                                />
                              )
                            }

                            return (
                              <SortableActionCard
                                key={action.id}
                                action={action}
                                onEdit={() => handleEditAction(action.id)}
                                onDelete={() => handleDeleteAction(action.id)}
                              />
                            )
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                    {/* Add Action Button - shown below actions when actions exist */}
                    {!creatingAction && editingActionId === null && (
                      <div className="flex justify-center">
                        <Button
                          onClick={handleCreateAction}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Action Step
                        </Button>
                      </div>
                    )}
                  </>
                ) : actionsList.length === 0 && !creatingAction && editingActionId === null ? (
                  <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-slate-50 dark:bg-gray-800 space-y-4">
                    <p className="text-sm text-slate-500 dark:text-gray-400">
                      No action steps yet. Add actions to perform on matching emails.
                    </p>
                    {/* Add Action Button - shown inside dashed box when no actions */}
                    <Button
                      onClick={handleCreateAction}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Action Step
                    </Button>
                  </div>
                ) : null}

                {/* Create New Action Form */}
                {creatingAction && (
                  <ActionEditor
                    action={{}}
                    onSave={handleSaveAction}
                    onCancel={handleCancelEditAction}
                    isSaving={savingAction}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Modal */}
        {showEditModal && (
          <WorkflowModal
            accountId={account.id}
            workflow={workflow}
            onClose={handleCloseModal}
          />
        )}

        {/* Delete Workflow Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{workflow.name}"? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Filter Dialog */}
        <AlertDialog open={deleteFilterId !== null} onOpenChange={(open) => !open && setDeleteFilterId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Filter</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this filter? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteFilter}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Action Dialog */}
        <AlertDialog open={deleteActionId !== null} onOpenChange={(open) => !open && setDeleteActionId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Action</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this action? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteAction}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
    </>
  )
}
